import { useEffect, useState } from 'react'
import {
  Drawer,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Switch,
  Paper,
  Badge,
  Divider,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  useAtividadeModeloCreate,
  useAtividadeModeloDelete,
  useAtividadeModeloList,
  useAtividadeModeloUpdate,
  useAplicarSugestoesNBR13971,
  useSugestoesNBR13971,
} from '../../hooks/usePlanosModelo'
import { EXIGENCIAS_FOTO, PERIODICIDADES, labelDe } from './constants'
import { extractApiError } from '../../lib/extractApiError'
import type {
  AtividadeModelo,
  AtividadeModeloCreateInput,
  ExigenciaFoto,
  PeriodicidadeAtividade,
  PlanoModelo,
} from '../../types/pmocPlanos'

interface Props {
  plano: PlanoModelo | null
  onClose: () => void
}

interface FormState {
  nome: string
  descricao: string
  periodicidade: PeriodicidadeAtividade
  periodicidade_customizada_dias: number | null
  exige_foto: ExigenciaFoto
  exige_medicao: boolean
  instrucoes_execucao: string
  criterio_esperado: string
  ordem: number
  ativo: boolean
}

const INICIAL: FormState = {
  nome: '',
  descricao: '',
  periodicidade: 'mensal',
  periodicidade_customizada_dias: null,
  exige_foto: 'nunca',
  exige_medicao: false,
  instrucoes_execucao: '',
  criterio_esperado: '',
  ordem: 0,
  ativo: true,
}

export default function AtividadesDrawer({ plano, onClose }: Props) {
  const planoId = plano?.id ?? ''
  const lista = useAtividadeModeloList(planoId)
  const create = useAtividadeModeloCreate(planoId)
  const update = useAtividadeModeloUpdate(planoId)
  const remove = useAtividadeModeloDelete(planoId)
  const tipoPrincipal = plano?.tipos_equipamento[0]
  const sugestoes = useSugestoesNBR13971(tipoPrincipal)
  const aplicarSugestoes = useAplicarSugestoesNBR13971(planoId)

  const [editandoId, setEditandoId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(INICIAL)

  useEffect(() => {
    if (!plano) {
      setEditandoId(null)
      setForm(INICIAL)
    }
  }, [plano])

  function entrarEdicao(at: AtividadeModelo) {
    setEditandoId(at.id)
    setForm({
      nome: at.nome,
      descricao: at.descricao,
      periodicidade: at.periodicidade,
      periodicidade_customizada_dias: at.periodicidade_customizada_dias,
      exige_foto: at.exige_foto,
      exige_medicao: at.exige_medicao,
      instrucoes_execucao: at.instrucoes_execucao,
      criterio_esperado: at.criterio_esperado,
      ordem: at.ordem,
      ativo: at.ativo,
    })
  }

  function resetForm() {
    setEditandoId(null)
    setForm({ ...INICIAL, ordem: (lista.data?.results.length ?? 0) + 1 })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.nome.trim()) {
      notifications.show({ color: 'red', message: 'Nome obrigatório.' })
      return
    }
    if (
      form.periodicidade === 'customizada_dias' &&
      (!form.periodicidade_customizada_dias ||
        form.periodicidade_customizada_dias <= 0)
    ) {
      notifications.show({
        color: 'red',
        message: 'Informe dias > 0 para periodicidade customizada.',
      })
      return
    }
    const payload: AtividadeModeloCreateInput = {
      ...form,
      periodicidade_customizada_dias:
        form.periodicidade === 'customizada_dias'
          ? form.periodicidade_customizada_dias
          : null,
    }
    try {
      if (editandoId) {
        await update.mutateAsync({ id: editandoId, data: payload })
        notifications.show({ color: 'green', message: 'Atividade atualizada.' })
      } else {
        await create.mutateAsync(payload)
        notifications.show({ color: 'green', message: 'Atividade criada.' })
      }
      resetForm()
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar atividade.'),
      })
    }
  }

  async function handleRemover(at: AtividadeModelo) {
    if (!confirm(`Inativar atividade "${at.nome}"?`)) return
    try {
      await remove.mutateAsync(at.id)
      notifications.show({ color: 'green', message: 'Atividade inativada.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao inativar.'),
      })
    }
  }

  async function handleAplicarSugestoes() {
    try {
      const resultado = await aplicarSugestoes.mutateAsync()
      notifications.show({
        color: 'green',
        title: 'Sugestões aplicadas',
        message:
          resultado.criadas > 0
            ? `${resultado.criadas} atividade(s) adicionada(s); ${resultado.ignoradas_ja_existentes} já existiam.`
            : 'Todas as atividades sugeridas já estavam no plano.',
      })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao aplicar sugestões',
        message: extractApiError(err, 'Não foi possível aplicar as sugestões.'),
      })
    }
  }

  const busy = create.isPending || update.isPending || aplicarSugestoes.isPending

  return (
    <Drawer
      opened={!!plano}
      onClose={onClose}
      title={plano ? `Atividades — ${plano.nome}` : ''}
      position="right"
      size="xl"
      overlayProps={{ backgroundOpacity: 0.4 }}
    >
      {plano && (
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            Atividade é um item executável dentro do plano. Cada atividade gera
            ocorrências futuras (uma por data prevista) quando o plano é aplicado
            a um PMOC.
          </Text>

          {plano.tipo_plano === 'equipamento' && tipoPrincipal && (
            <Paper p="sm" withBorder bg="blue.0">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <div style={{ flex: 1, minWidth: 260 }}>
                  <Text fw={600}>Sugestões ABNT NBR 13971</Text>
                  <Text size="sm" c="dimmed">
                    O tipo de equipamento indica os componentes aplicáveis. A
                    periodicidade continua editável pelo RT conforme fabricante,
                    uso e criticidade.
                  </Text>
                  {sugestoes.data && (
                    <Group gap={6} mt="xs" wrap="wrap">
                      <Badge variant="light">
                        {sugestoes.data.total} atividades sugeridas
                      </Badge>
                      {sugestoes.data.atividades
                        .filter((atividade) => atividade.codigo_nbr)
                        .slice(0, 8)
                        .map((atividade) => (
                          <Badge
                            key={`${atividade.codigo_nbr}-${atividade.nome}`}
                            size="sm"
                            color="teal"
                            variant="outline"
                            title={atividade.nome}
                          >
                            {atividade.codigo_nbr}
                          </Badge>
                        ))}
                      {sugestoes.data.total > 8 && (
                        <Text size="xs" c="dimmed">
                          e outras atividades aplicáveis
                        </Text>
                      )}
                    </Group>
                  )}
                </div>
                <Button
                  size="xs"
                  onClick={handleAplicarSugestoes}
                  loading={aplicarSugestoes.isPending}
                  disabled={sugestoes.isLoading || !sugestoes.data?.total}
                >
                  Preencher atividades sugeridas
                </Button>
              </Group>
            </Paper>
          )}

          <Paper p="sm" withBorder>
            <Text fw={600} mb="xs">
              {editandoId ? 'Editar atividade' : 'Nova atividade'}
            </Text>
            <form onSubmit={handleSubmit}>
              <Stack gap="xs">
                <TextInput
                  label="Nome"
                  required
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.currentTarget.value })}
                  data-testid="atividade-nome"
                />
                <Textarea
                  label="Descrição"
                  autosize
                  minRows={1}
                  value={form.descricao}
                  onChange={(e) =>
                    setForm({ ...form, descricao: e.currentTarget.value })
                  }
                />
                <Group grow>
                  <Select
                    label="Periodicidade"
                    required
                    data={PERIODICIDADES}
                    value={form.periodicidade}
                    onChange={(v) =>
                      v &&
                      setForm({
                        ...form,
                        periodicidade: v as PeriodicidadeAtividade,
                      })
                    }
                    data-testid="atividade-periodicidade"
                  />
                  {form.periodicidade === 'customizada_dias' && (
                    <NumberInput
                      label="Dias"
                      required
                      min={1}
                      value={form.periodicidade_customizada_dias ?? ''}
                      onChange={(v) =>
                        setForm({
                          ...form,
                          periodicidade_customizada_dias:
                            typeof v === 'number' ? v : null,
                        })
                      }
                    />
                  )}
                  <NumberInput
                    label="Ordem"
                    min={0}
                    value={form.ordem}
                    onChange={(v) =>
                      setForm({ ...form, ordem: typeof v === 'number' ? v : 0 })
                    }
                  />
                </Group>
                <Textarea
                  label="Instruções de execução"
                  autosize
                  minRows={1}
                  value={form.instrucoes_execucao}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      instrucoes_execucao: e.currentTarget.value,
                    })
                  }
                />
                <TextInput
                  label="Critério esperado"
                  placeholder="Ex.: filtros limpos, ΔP < 250Pa"
                  value={form.criterio_esperado}
                  onChange={(e) =>
                    setForm({ ...form, criterio_esperado: e.currentTarget.value })
                  }
                />
                <Group grow align="flex-end">
                  <Select
                    label="Exige foto"
                    data={EXIGENCIAS_FOTO}
                    value={form.exige_foto}
                    onChange={(v) =>
                      v && setForm({ ...form, exige_foto: v as ExigenciaFoto })
                    }
                    data-testid="atividade-exige-foto"
                  />
                  <Switch
                    label="Exige medição"
                    checked={form.exige_medicao}
                    onChange={(e) =>
                      setForm({ ...form, exige_medicao: e.currentTarget.checked })
                    }
                  />
                  <Switch
                    label="Ativa"
                    checked={form.ativo}
                    onChange={(e) =>
                      setForm({ ...form, ativo: e.currentTarget.checked })
                    }
                  />
                </Group>
                {form.exige_medicao && (
                  <Text size="xs" c="dimmed">
                    Medições esperadas (faixas/limites) são configuradas no PMOC,
                    em cima do snapshot da atividade, depois que o plano é aplicado.
                  </Text>
                )}
                <Group justify="flex-end">
                  {editandoId && (
                    <Button variant="default" onClick={resetForm} disabled={busy}>
                      Cancelar edição
                    </Button>
                  )}
                  <Button
                    type="submit"
                    loading={busy}
                    data-testid="atividade-submit"
                  >
                    {editandoId ? 'Salvar' : 'Adicionar atividade'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>

          <Divider label="Atividades existentes" labelPosition="left" />

          {lista.isLoading ? (
            <Text c="dimmed">Carregando…</Text>
          ) : (lista.data?.results.length ?? 0) === 0 ? (
            <Text c="dimmed">Nenhuma atividade cadastrada ainda.</Text>
          ) : (
            <Stack gap="xs">
              {lista.data?.results.map((at) => (
                <Paper key={at.id} p="sm" withBorder>
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <div style={{ flex: 1 }}>
                      <Group gap="xs" wrap="wrap">
                        <Text fw={600}>{at.nome}</Text>
                        <Badge size="sm" variant="light">
                          {labelDe(at.periodicidade, PERIODICIDADES)}
                          {at.periodicidade === 'customizada_dias' &&
                          at.periodicidade_customizada_dias
                            ? ` (${at.periodicidade_customizada_dias}d)`
                            : ''}
                        </Badge>
                        {at.exige_foto !== 'nunca' && (
                          <Badge size="sm" color="orange" variant="light">
                            📷 Foto {at.exige_foto === 'obrigatoria' ? '(obrigatória)' : '(opcional)'}
                          </Badge>
                        )}
                        {at.exige_medicao && (
                          <Badge size="sm" color="cyan" variant="light">
                            📊 Medição
                          </Badge>
                        )}
                        {!at.ativo && (
                          <Badge size="sm" color="gray">
                            inativa
                          </Badge>
                        )}
                      </Group>
                      {at.descricao && (
                        <Text size="sm" c="dimmed" mt={2}>
                          {at.descricao}
                        </Text>
                      )}
                      {at.criterio_esperado && (
                        <Text size="xs" mt={2}>
                          <strong>Critério:</strong> {at.criterio_esperado}
                        </Text>
                      )}
                    </div>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => entrarEdicao(at)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => handleRemover(at)}
                      >
                        Inativar
                      </Button>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  )
}
